<template>
  <ProductsFormController
    v-if="route.meta.subpage === 'ProductCatalogueForm'"
    @update:products="fetchProducts(currentOffset)"
  />
  <article
    v-else
    v-bind="$attrs"
    class="d-flex h-100 w-100 overflow-hidden"
    data-testid="product-catalog.products-&-services.main-article"
  >
    <CategoryListGroup
      :loading="loading"
      :categories="getProductCategories"
      :sub-categories="getProductSubCategories"
      :groups="getProductGroups"
      :products="getProducts"
      :is-product-offering="false"
      :open-categories="openCategories"
      :open-sub-categories="openSubCategories"
      :selected-sku="selectionSkuWhole"
      class="me-3 flex-grow-1"
      data-testid="product-catalog.products-&-services.category-navigation"
      @update-active-list-id="(id: string) => updateActiveCategorySku(id)"
      @update-open-lists="(cats: number[], subCats: number[]) => updateLists(cats, subCats)"
    />
    <ProductsDataTable
      :loading="loading || loadingProducts"
      :products="products"
      :product-types="getProductTypes"
      :selected-sku="selectedSku"
      :error-message="errorMessage"
      :selected-lifecycle="selectedFilterLifecycle"
      class="flex-grow-1 align-self-start"
      @update-lifecycle="updateLifecycleFilter"
      @search="(search: string) => searchProducts(search)"
      @update-product="(id: any) => router.push({path: `update/${id}`})"
      @update-product-offering="(id: any) => router.push({path: `update/${id}`, query: {po: 1}})"
      @delete-products="(ids: any) => deleteDialogOpen(ids, 'product')"
      @delete-product-offerings="(ids: any) => deleteDialogOpen(ids, 'productOffering')"
      @update-offset="(offset: any) => updateOffset(offset)"
      @update-page-limit="(size: number) => updatePageSize(size)"
      @create="
        router.push({
          path: 'create',
          query: {
            cid: selectedCategoryId || defaultSelected?.id,
            scid: selectedSubCategoryId,
            gid: selectedGroupId,
          },
        })
      "
    />
  </article>
  <DeleteDialog
    :open="deleteDialogIsOpen"
    type="product"
    :loading="loadingProducts"
    @delete="() => deleteItem()"
    @close="() => (deleteDialogIsOpen = false)"
  />
</template>
<script lang="ts" setup>
import {scrollTop} from '@/sphere/scroll';
const route = useRoute();
const router = useRouter();

// Stores
const productCategoriesStore = useProductCategoriesStore();
const productSubCategoriesStore = useProductSubCategoriesStore();
const productGroupsStore = useProductGroupsStore();
const productsStore = useProductsStore();
const productTypesStore = useProductTypesStore();

// Store refs
const {products} = storeToRefs(productsStore);

const getProductCategories = computed(() => productCategoriesStore.getProductCategories);
const getProductSubCategories = computed(() => productSubCategoriesStore.getProductSubCategories);
const getProductGroups = computed(() => productGroupsStore.getProductGroups);
const getProducts = computed(() => productsStore.getProducts);
const getProductTypes = computed(() => productTypesStore.getProductTypes);

// refs
const loading = ref(true);
const errorMessage = ref('');
const loadingProducts = ref(true);
const deleteDialogIsOpen = ref(false);
const idsToDelete = ref<string[]>([]);
const typeToDelete = ref('');
const search = ref('');
const pageSize = ref(25);
const currentOffset = ref(0);
const openCategories = ref<number[]>([]);
const openSubCategories = ref<number[]>([]);
const selectedFilterLifecycle = ref<string>('');

const updateLists = (cats: number[], subCats: number[]) => {
  openCategories.value = cats;
  openSubCategories.value = subCats;
};

/**
 * Selected SKUs used as query params in Product forms route
 */
const selectedSku = ref<string>();
const selectedCategoryId = ref<number>();
const defaultSelected: globalThis.ComputedRef<ProductCategory | undefined> = computed(() =>
  getProductCategories.value.length > 0 ? getProductCategories.value[0] : undefined,
);
const selectedSubCategoryId = ref<number>();
const selectedGroupId = ref<number>();

const selectionSkuWhole = computed(() => {
  const sku = [];
  if (selectedCategoryId.value) {
    sku.push(getProductCategories.value.find(({id}) => id === selectedCategoryId.value)?.sku);
  }
  if (selectedSubCategoryId.value) {
    sku.push(getProductSubCategories.value.find(({id}) => id === selectedSubCategoryId.value)?.sku);
  }
  if (selectedGroupId.value) {
    sku.push(getProductGroups.value.find(({id}) => id === selectedGroupId.value)?.sku);
  }
  return sku.length > 0 ? sku.join('-') : selectedSku.value;
});

// computed
const skus = computed(() => {
  return getProductCategories.value.length > 0 &&
    getProductSubCategories.value.length > 0 &&
    getProductGroups.value.length > 0
    ? [
        ...getProductCategories.value,
        ...getProductSubCategories.value,
        ...getProductGroups.value,
      ].map(({sku}) => sku)
    : [];
});

const deleteDialogOpen = (ids: string[], type: string) => {
  deleteDialogIsOpen.value = true;
  idsToDelete.value = ids;
  typeToDelete.value = type;
};

/**
 * Deletes a product with deleteProduct in products store
 */
const deleteItem = async () => {
  const promises =
    typeToDelete.value === 'product'
      ? idsToDelete.value.map((id) => productsStore.deleteProduct(Number(id)))
      : idsToDelete.value.map((id) => productsStore.deleteProductOffering(Number(id)));
  loadingProducts.value = true;
  try {
    await Promise.all(promises);
  } catch (error: any) {
    if (error === 'Unauthorized' || error === 'Not authenticated') {
      try {
        await Promise.all(promises);
      } catch (error: any) {
        errorMessage.value = error;
      }
    } else {
      errorMessage.value = error;
    }
  } finally {
    deleteDialogIsOpen.value = false;
    loadingProducts.value = false;
  }
};

/**
 * Makes requests for products data
 */
const fetchProducts = async (offset: number, sku_str?: string) => {
  loadingProducts.value = true;
  try {
    currentOffset.value = offset;
    await productsStore.readProducts({
      limit: pageSize.value,
      offset: currentOffset.value,
      sku_str,
      search: search.value,
      lifecycle_stage: selectedFilterLifecycle.value,
    });
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    loadingProducts.value = false;
  }
};

const searchProducts = (newSearch: string) => {
  search.value = newSearch;
  fetchProducts(0, selectedSku.value);
};

const updateLifecycleFilter = (newLifecycle: string) => {
  selectedFilterLifecycle.value = newLifecycle;
  fetchProducts(0, selectedSku.value);
};

/**
 * Sets selected category ids, refs used as query params when navigating to products form
 */
const setSelectedCategories = (cSku?: string, scSku?: string, gSku?: string) => {
  selectedCategoryId.value =
    getProductCategories.value.length > 0 && cSku !== undefined
      ? getProductCategories.value.find(({sku}) => sku === cSku)?.id
      : undefined;
  selectedSubCategoryId.value =
    getProductSubCategories.value.length > 0 && scSku !== undefined
      ? getProductSubCategories.value.find(({sku}) => sku === scSku)?.id
      : undefined;
  selectedGroupId.value =
    getProductGroups.value.length > 0 && gSku !== undefined
      ? getProductGroups.value.find(({sku}) => sku === gSku)?.id
      : undefined;
};

/**
 * Finds all products that can be found using an aggregated sku id
 * @param {string} id - sku id (eg. MW-MWEP-MWEPT1)
 */
const updateActiveCategorySku = (id: string) => {
  scrollTop();
  const [categorySku, subCategorySku, groupSku] = id.split('-');
  if (skus.value.length > 0) {
    if (groupSku) {
      selectedSku.value = skus.value.find((sku) => sku === groupSku);
    } else if (subCategorySku) {
      selectedSku.value = skus.value.find((sku) => sku === subCategorySku);
    } else if (categorySku) {
      selectedSku.value = skus.value.find((sku) => sku === categorySku);
    }
    setSelectedCategories(categorySku, subCategorySku, groupSku);
    // If the active sku changed, then we have to fetch due to products being paginated
    if (products.value !== null) fetchProducts(0, selectedSku.value);
  }
};

const updatePageSize = (size: number) => {
  pageSize.value = size;
  fetchProducts(0, selectedSku.value);
};

const updateOffset = async (offset: number) => {
  try {
    await fetchProducts(offset, selectedSku.value);
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    scrollTop();
  }
};

/**
 * Called on initial mount
 */
const fetchData = async () => {
  try {
    await Promise.all([
      productCategoriesStore.readProductCategories(),
      productSubCategoriesStore.readProductSubCategories(),
      productGroupsStore.readProductGroups(),
      productTypesStore.readProductTypes(),
    ]);
  } catch (error: any) {
    errorMessage.value = error;
  } finally {
    loading.value = false;
    loadingProducts.value = false;
  }
};

onMounted(async () => {
  await fetchData();
  /**
   * When the overview is mounted, CategoryListGroup.vue will select the first
   * category to be active by default. We use defaultSelected.value in the same
   * way to fetch the first page of products
   */
  if (defaultSelected.value !== undefined) {
    selectedSku.value = defaultSelected.value.sku;
    await fetchProducts(defaultOffset, defaultSelected.value.sku);
  }
});
</script>
